import { useState, useEffect, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
import { cn } from '../../lib/utils';
import { apiClient } from '../../lib/api-client';
import { UserNotificationsDialog } from '../notifications/UserNotificationsDialog';
import { useAuth } from '../../contexts/AuthContext';
import { useActivityHeartbeat } from '../../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../../hooks/useCacheEvents';

export function NotificationsBell() {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // User preference opt-out
  const teamsInboxEnabled = (user as any)?.preferences?.notifications?.teams_inbox !== false;

  // Ensure the backend treats this user as "active" for the user_notifications cache while the user is on the portal.
  // This allows the backend cache refresh scheduler to run at the configured active interval (e.g. 60s),
  // so unread counts and the dialog contents update without manual refresh.
  // Must NOT be conditional (hooks order). Gate via enabled flag only.
  useActivityHeartbeat('cache:user_notifications', !!teamsInboxEnabled && !!user?.email, 5000);

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.email) return;
    
    try {
      // First check if user has access (only on first load)
      if (hasAccess === null) {
        const accessRes = await apiClient.checkUserGraphApiAccess();
        setHasAccess(accessRes.hasAccess);
        if (!accessRes.hasAccess) {
          setIsLoading(false);
          return;
        }
      } else if (!hasAccess) {
        return;
      }

      const res = await apiClient.getUserNotificationsUnreadCount();
      setUnreadCount(res.unreadCount || 0);
    } catch (e) {
      console.error('Failed to fetch unread count:', e);
    } finally {
      setIsLoading(false);
    }
  }, [user?.email, hasAccess]);

  // Initial fetch
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Standard cache model: refresh unread count only when backend emits user_notifications cache events.
  useCacheEvents({
    enabled: !!teamsInboxEnabled && !!user?.email,
    cacheNames: ['user_notifications'],
    onEvent: (e) => {
      const email = String(user?.email || '').trim().toLowerCase();
      const affected = Array.isArray(e.affectedUsers) ? e.affectedUsers.map((x) => String(x).trim().toLowerCase()) : [];
      if (affected.length > 0 && email && !affected.includes(email)) return;
      void fetchUnreadCount();
    },
  });

  // Handle unread count updates from dialog
  const handleUnreadCountChange = (count: number) => {
    setUnreadCount(count);
  };

  // Render even if access isn't granted yet; the dialog will explain what to do.

  if (!teamsInboxEnabled) {
    return null;
  }

  return (
    <>
      <Tooltip content="Teams Notifications" side="bottom">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDialogOpen(true);
          }}
          className={cn(
            'relative flex items-center justify-center p-2 transition-colors rounded-lg',
            'hover:bg-gray-100 dark:hover:bg-gray-800',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900'
          )}
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          <Bell
            className={cn(
              'w-5 h-5',
              hasAccess === false ? 'text-gray-400 dark:text-gray-600' : 'text-gray-600 dark:text-gray-400'
            )}
          />
          
          {/* Unread badge */}
          {unreadCount > 0 && (
            <span className={cn(
              'absolute -top-0.5 -right-0.5 flex items-center justify-center',
              'min-w-[18px] h-[18px] px-1 text-xs font-bold',
              'bg-red-500 text-white rounded-full',
              unreadCount > 99 && 'text-[10px]'
            )}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </Tooltip>

      <UserNotificationsDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onUnreadCountChange={handleUnreadCountChange}
      />
    </>
  );
}
